import { MapGeoJSONFeature } from "maplibre-gl";
import { useEffect, useState } from "react";
import {
  CampgroundDriveTimeSearchRequest,
  Feature,
} from "../../api-clients/iwannacamp-api";
import { useQuery } from "../../store/slices/campgroundSearch";

const getCampgroundName = (campground: MapGeoJSONFeature | Feature) => {
  const properties = campground.properties;
  return properties?.name || "Unnamed campground";
};

const getCampgroundId = (campground: MapGeoJSONFeature | Feature) => {
  const properties = campground.properties;
  return properties?.sqid;
};

const useCampgroundDirectionsUrl = (campground?: MapGeoJSONFeature) => {
  const query = useQuery();
  const [directionsUrl, setDirectionsUrl] = useState<string | undefined>();

  useEffect(() => {
    if (query && campground && campground.geometry) {
      const pointGeometry = campground.geometry as GeoJSON.Point;
      const cdtsrQuery = query as CampgroundDriveTimeSearchRequest;

      let origin = "";
      if (cdtsrQuery.latDeg && cdtsrQuery.lonDeg) {
        origin = `${cdtsrQuery.latDeg},${cdtsrQuery.lonDeg}`;
      }

      const destination = `${pointGeometry.coordinates[1]},${pointGeometry.coordinates[0]}`;
      setDirectionsUrl(
        `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`
      );
    }
  }, [campground, query]);

  return directionsUrl;
};

const getCampgroundWebsiteUrl = (campground: MapGeoJSONFeature) => {
  const properties = campground.properties;
  return properties["website"];
};

const getCampgroundLocationString = (campground: MapGeoJSONFeature) => {
  const properties = campground.properties;
  const city = properties["city"];
  const county = properties["county"];
  const state = properties["state"];

  let locationString = "";
  if (city) {
    locationString += city;
  } else if (county) {
    if (locationString) {
      locationString += ", ";
    }
    locationString += county;
  }
  if (state) {
    if (locationString) {
      locationString += ", ";
    }
    locationString += state;
  }

  return locationString;
};

const getCampgroundOperatorString = (campground: MapGeoJSONFeature) => {
  const properties = campground.properties;
  const operator = properties["operator"];
  const owner = properties["iwc:owner"];
  const ownerUnit = properties["iwc:owner_unit"];
  const ownerType = properties["iwc:owner_type"];
  const county = properties["county"];

  let operatorString = "";

  if (owner && ownerType) {
    // Format based on ownership type
    switch (ownerType) {
      case "federal":
        // Federal: "<Agency>: <unit>"
        operatorString = owner;
        if (ownerUnit) {
          operatorString += `: ${ownerUnit}`;
        }
        break;
      case "state":
        // State: "State Park: <unit>"
        operatorString = "State Park";
        if (ownerUnit) {
          operatorString += `: ${ownerUnit}`;
        }
        break;
      case "city":
        // City: "City Park: <unit>"
        operatorString = "City Park";
        if (ownerUnit) {
          operatorString += `: ${ownerUnit}`;
        }
        break;
      case "county":
        // County: "County Park: <county>"
        operatorString = "County Park";
        if (county) {
          operatorString += `: ${county}`;
        } else if (ownerUnit) {
          operatorString += `: ${ownerUnit}`;
        }
        break;
      case "regional":
        // Regional: "Regional Park: <unit>"
        operatorString = "Regional Park";
        if (ownerUnit) {
          operatorString += `: ${ownerUnit}`;
        }
        break;
      default:
        if (ownerUnit) {
          operatorString = ownerUnit;
        }
        break;
    }
  } else if (operator) {
    // Fallback to OSM operator tag
    operatorString = operator;
  }

  return operatorString;
};

export {
  getCampgroundId,
  getCampgroundLocationString,
  getCampgroundName,
  getCampgroundOperatorString,
  getCampgroundWebsiteUrl,
  useCampgroundDirectionsUrl,
};
